// src/components/charts/TicketingCharts.jsx
import React from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import kpiService from '../../services/kpiService';
import { formatCurrency, formatNumber } from '../../utils/formatters';

/**
 * Componente que agrupa los gráficos de Ticketing (Imagen 5)
 * - Ventas de tickets (barras horizontales - izquierda)
 * - Principales métricas (tabla - derecha)
 */
const TicketingCharts = ({ fechaInicio, fechaFin, empresaId }) => {
  const [ticketingData, setTicketingData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await kpiService.getTicketing({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        if (response.success) {
          setTicketingData(response.data);
        }
      } catch (err) {
        console.error('Error fetching ticketing data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fechaInicio, fechaFin, empresaId]);

  if (loading) {
    return (
      <Row className="g-3 mb-4">
        <Col xs={12} className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando datos...</p>
        </Col>
      </Row>
    );
  }

  const formatMetricValue = (metricaObj, valor) => {
  if (metricaObj.metrica.includes('Precio medio') || metricaObj.unidad === '€') {
    return `${valor}€`;
  } else if (metricaObj.unidad === '%') {
    return `${valor}%`;
  } else {
    return formatNumber(valor);
  }
};

  return (
    <Row className="g-3 mb-4">
      {/* Ventas de tickets - Comparativa real vs proyectado */}
      <Col lg={6}>
        {ticketingData?.ventasTickets && (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Ventas de tickets</h5>
              <div className="d-flex flex-column gap-4">
                {ticketingData.ventasTickets.map((item, idx) => {
                  const percentageReal = (item.real / item.proyectado) * 100;
                  const isNegative = item.metrica.includes('devueltos');
                  
                  return (
                    <div key={idx}>
                      <div className="mb-2">
                        <div className="small fw-medium mb-1">{item.metrica}</div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small">
                            {formatMetricValue(item, item.real)} | {formatMetricValue(item, item.proyectado)}
                          </span>
                        </div>
                      </div>
                      <div className="position-relative" style={{ height: '32px' }}>
                        {/* Barra de fondo (proyectado) */}
                        <div
                          className="position-absolute w-100 rounded"
                          style={{
                            height: '100%',
                            backgroundColor: '#e5e7eb',
                          }}
                        />
                        {/* Barra de real */}
                        <div
                          className="position-absolute rounded"
                          style={{
                            height: '100%',
                            width: `${Math.min(percentageReal, 100)}%`,
                            backgroundColor: isNegative ? '#ef4444' : item.color,
                            transition: 'width 0.6s ease-out',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Principales métricas de ticketing */}
      <Col lg={6}>
        {ticketingData?.metricasTicketing && (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Principales métricas</h5>
              <div className="d-flex flex-column gap-3">
                {ticketingData.metricasTicketing.map((metrica, idx) => (
                  <div key={idx} className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                    <div>
                      <div className="small text-muted mb-1">{metrica.metrica}</div>
                      <div className="h4 mb-0 fw-bold">
                        {metrica.valor}{metrica.unidad}
                      </div>
                    </div>
                    <div className="text-end">
                      <span 
                        className="badge"
                        style={{ 
                          backgroundColor: metrica.isPositive ? '#10b981' : '#ef4444',
                          fontSize: '0.875rem',
                          padding: '0.5rem 0.75rem'
                        }}
                      >
                        {metrica.isPositive ? '+' : ''}{metrica.variacion}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
};

export default TicketingCharts;